import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import ActivityComponentMap from "~/constants/ActivityComponentMap";
import PIN_TAB_KEYS from "~/constants/pin_tab_keys";
import { canUserPinActivity, PIN_LIMIT } from "~/helpers/pinHelpers";

const STORAGE_KEY = "isPinnedSectionOpen";

const PinnedActivities = ({
  pinnedActivities = [],
  pinnedCount = 0,
  activeTab = "all",
  lead = {},
  currentUser = {},
  onUnpin,
  setCurrentFormWithData,
  toggleActivityInternal,
  removeActivity,
  pinLoadingIds = [],
}) => {
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  const onCollapseClick = useCallback(
    (e) => {
      e.preventDefault();
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newIsOpen));
      } catch {
        // localStorage unavailable
      }
    },
    [isOpen],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onCollapseClick(e);
      }
    },
    [onCollapseClick],
  );

  // Filter pinned activities by active tab
  const filteredPinned = useMemo(() => {
    // Tabs not in PIN_TAB_KEYS (changes, tasks) have no pinnable types — hide section
    if (!(activeTab in PIN_TAB_KEYS)) {
      return [];
    }

    const tabKeys = PIN_TAB_KEYS[activeTab];
    if (tabKeys === null) {
      // "all" tab — show all pinned activities
      return pinnedActivities;
    }
    return pinnedActivities.filter((a) => tabKeys.includes(a.key));
  }, [pinnedActivities, activeTab]);

  const canUserPin = useCallback(
    () => canUserPinActivity(currentUser),
    [currentUser],
  );

  // Don't render if no pinned activities for current tab or on changes tab
  if (filteredPinned.length === 0) {
    return null;
  }

  return (
    <div className="pinned-activities-section mb15">
      <div
        className="pinned-section-header d-flex align-items-center justify-content-between"
        onClick={onCollapseClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="d-flex align-items-center">
          <FontAwesomeIcon
            icon={["fal", "fa-thumbtack"]}
            className="pinned-section-icon text-secondary mr5"
          />
          <span className="pinned-section-label text-uppercase text-secondary font-weight-bold">
            Pinned ({filteredPinned.length})
          </span>
        </div>
        <FontAwesomeIcon
          icon={["far", isOpen ? "fa-minus" : "fa-plus"]}
          className="pinned-section-toggle text-secondary"
        />
      </div>

      <Collapse in={isOpen} mountOnEnter unmountOnExit>
        <div>
          {filteredPinned.map((activity) => {
            const ActivityComponentInfo = ActivityComponentMap[activity.key];
            if (!ActivityComponentInfo) {
              return null;
            }

            const { Component: ActivityComponent, props: activityProps = {} } =
              ActivityComponentInfo;
            const canPin = canUserPin();
            const isPinLoading = pinLoadingIds.includes(activity.id);

            return (
              <div
                key={`pinned-${activity.id}`}
                className="pinned-activity-card mt10"
              >
                <ActivityComponent
                  activity={activity}
                  lead={lead}
                  activeTab={activeTab}
                  {...activityProps}
                  setCurrentFormWithData={setCurrentFormWithData}
                  toggleActivityInternal={toggleActivityInternal}
                  removeActivity={removeActivity}
                  isPinned
                  isAtLimit={pinnedCount >= PIN_LIMIT}
                  pinnedCount={pinnedCount}
                  onPinToggle={() => onUnpin(activity)}
                  canPin={canPin}
                  canUnpin={canPin}
                  isPinLoading={isPinLoading}
                />
              </div>
            );
          })}
        </div>
      </Collapse>
    </div>
  );
};

export default PinnedActivities;
